import { timeout } from 'widgets/toolbox/util';
import { getViewType } from 'widgets/toolbox/viewtype';

function CoreFocusHighlighterClassCreator(FocusHighlighter: import('widgets/global/FocusHighlighter').TFocusHighlighter) {
    class CoreFocusHighlighter extends FocusHighlighter {
        /**
         * @description Tracks whether the last user interaction was via keyboard.
         * Used to distinguish keyboard navigation from touch/mouse on mobile.
         */
        private lastInteractionWasKeyboard = false;

        prefs() {
            return {
                headerId: 'headercontent',
                ...super.prefs()
            };
        }

        /**
         * @description Widget initialization logic
         */
        init(): void {
            super.init();

            this.initInputModalityTracking();
            this.ev('focusin', this.checkFocusVisible, document);
        }

        /**
         * @description Initializes pointer and keyboard event listeners to track
         * whether the user is navigating via keyboard or touch/mouse.
         * This is used to correctly enable the focus highlighter only when
         * keyboard/accessibility navigation is detected on mobile devices.
         *
         * On mobile, `:focus-visible` matches both touch taps and keyboard focus,
         * so we cannot rely on it alone to detect external keyboard usage.
         */
        initInputModalityTracking(): void {
            document.addEventListener('pointerdown', () => {
                this.lastInteractionWasKeyboard = false;
            });

            document.addEventListener('keydown', (e: KeyboardEvent) => {
                // Only navigation/interaction keys signal keyboard accessibility usage
                const navKeys = ['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'];

                if (navKeys.includes(e.key)) {
                    this.lastInteractionWasKeyboard = true;
                }
            });
        }

        /**
         * @description Determines whether an external keyboard is likely active on
         * a mobile/tablet device, using two signals:
         * 1. Whether a navigation key was the last interaction (`lastInteractionWasKeyboard`)
         * 2. Whether the device reports a fine pointer (mouse/stylus), suggesting
         *    non-touch primary input (`pointer: fine` media query)
         *
         * @returns true if keyboard or fine-pointer input is detected
         */
        isExternalKeyboardActive(): boolean {
            const hasFinePointer = window.matchMedia('(pointer: fine)').matches;

            return this.lastInteractionWasKeyboard || hasFinePointer;
        }

        /**
         * @description Handles focus change on page
         * @listens dom#focusin
         */
        handleFocus(): void {
            if (!(document.activeElement instanceof HTMLElement)) {
                return;
            } // Needed only for TS linter

            const focusedElement = document.activeElement;

            if (!this.isValidTarget(focusedElement) || (this.isTextInput(focusedElement) && !this.keyboardModality)) {
                return;
            }

            this.detectHurryNavigation();
            this.moveTo(focusedElement);

            const header = document.getElementById(this.prefs().headerId);
            const headerHeight = header?.offsetHeight ?? 0;

            if (focusedElement && header && !header.contains(focusedElement)) {
                const elRect = focusedElement.getBoundingClientRect();

                if (elRect.bottom <= headerHeight && elRect.bottom <= window.innerHeight) {
                    window.scrollBy(0, -(headerHeight + Math.abs(elRect.top)));
                }
            }
            // We need to recheck focused element position since coords could
            // be changed during scroll in carousels, animation, dynamic page changes

            if (this.recheckTimeout) {
                this.recheckTimeout();
            }

            if (this.recheckSecondTimeout) {
                this.recheckSecondTimeout();
            }

            this.recheckTimeout = timeout(() => this.moveTo(document.activeElement as HTMLElement), 400);

            this.recheckSecondTimeout = timeout(() => this.moveTo(document.activeElement as HTMLElement), 800);
        }

        /**
         * @description Moves `highlighter` (a border box) in place of focused element
         * @param focusedElement - element, which gets focus
         */
        moveTo(focusedElement: HTMLElement) {
            if (!(focusedElement instanceof HTMLElement)) {
                return;
            }

            const highlighterNode = this.ref('highlighter').get();

            if (!highlighterNode) {
                return;
            }

            const targetRectangle = focusedElement.getBoundingClientRect();
            const style = getComputedStyle(focusedElement);
            const parentStyle = focusedElement.parentElement ? getComputedStyle(focusedElement.parentElement) : null;
            const isTransformed = style.transform.includes('matrix') || (parentStyle?.transform.includes('matrix') ?? false);
            const targetWidth = isTransformed ? targetRectangle.width : focusedElement.offsetWidth;
            const targetHeight = isTransformed ? targetRectangle.height : focusedElement.offsetHeight;
            const targetTop = targetRectangle.top + window.scrollY;
            const targetLeft = targetRectangle.left + window.scrollX;
            const borderWidth = 3;
            const outlineWidth = 3;
            const documentWidth = this.getDocumentWidth();
            const isIntersectsViewport = targetLeft + targetWidth + borderWidth + outlineWidth > documentWidth;

            if (
                focusedElement === this.lastFocusedElement &&
                this.lastFocusedElementCoords === '' + targetTop + targetLeft + targetWidth + targetHeight
            ) {
                // If we come from coords recheck do not reapply changes
                return;
            }

            const highlighterStyle = highlighterNode.style;

            highlighterStyle.top = `${targetTop - 5}px`;
            highlighterStyle.left = `${targetLeft - 5}px`;
            highlighterStyle.width = `${targetWidth + 4}px`;
            highlighterStyle.height = `${targetHeight + 4}px`;

            if (focusedElement instanceof HTMLInputElement && focusedElement.type === 'radio' && focusedElement.role !== 'radio') {
                highlighterStyle.borderRadius = '50%';
            } else {
                highlighterStyle.borderRadius = '3px';
            }

            if (isIntersectsViewport) {
                highlighterStyle.left = `${targetLeft + borderWidth}px`;
                highlighterStyle.width = `${documentWidth - targetLeft - (borderWidth + outlineWidth) * 2}px`;
            }

            this.lastFocusedElementCoords = '' + targetTop + targetLeft + targetWidth + targetHeight;

            this.lastFocusedElement = focusedElement;
        }

        /**
         * @description Checks if the focused element matches :focus-visible
         * and enables highlighter if so
         * @listens dom#focusin
         */
        checkFocusVisible(): void {
            const activeElement = document.activeElement;
            const viewType = getViewType();
            const isMobile = viewType === 'small' || viewType === 'medium';

            /*
            Mobile with external keyboard may not trigger 'Tab' keydown event,
            so we check :focus-visible as a fallback — but only when we have
            additional confirmation that keyboard/fine-pointer input is active.
            This prevents touch taps from incorrectly triggering the highlighter.
             */
            if (isMobile && activeElement instanceof HTMLElement && activeElement.matches(':focus-visible') && this.isExternalKeyboardActive()) {
                this.enableHighlighter();
            }
        }

        handleTrackClick() {
            // no-op: handled by CoreHeader when header context is loaded
        }
    }

    return CoreFocusHighlighter;
}

export type TCoreFocusHighlighter = ReturnType<typeof CoreFocusHighlighterClassCreator>;

export type TCoreFocusHighlighterInstance = InstanceType<TCoreFocusHighlighter>;

export default CoreFocusHighlighterClassCreator;
